import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router-dom';
import { setUserInfo, UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import useBundleTranslation from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { setPageMenu } from 'store/pageMenuSlice';
import { removeBreadcrumbs, setBreadcrumbs, setBreadcrumbsInstanceKey } from 'store/breadcrumbsSlice';
import { useConfigConstantValue, useIsConfigConstantEqual } from 'hooks/useConfigConstant';
import { setHelpLink } from 'store/helpLinkSlice';
import { removePageTitle, setPageTitle, setPageTitleInstanceKey } from 'store/pageTitleSlice';
import { licenseKeyAPI } from 'api/liceseKey';
import { LicensePopup } from 'components/common/license-popup/LicensePopup';
import MobilePagePopup from 'components/common/mobile-page-popup/MobilePagePopup';
import TourManager from 'app/tour/TourManager';
import { WarnMismatchLoginPopup } from 'components/common/warn-mismatch-login-popup/WarnMismatchLoginPopup';
import { useIsAdmin, useIsPowerUser } from 'hooks/useUserPrivilege';
import CommandMenu from 'components/command-menu/CommandMenu';
import LoginRedirect from 'routes/LoginRedirect';
import { addCommandMenuNewRecentItem } from 'components/command-menu/CommandMenuStorage';
import { checkIsAppPage } from 'tools/loaction';

export default function ProtectedRoute() {
    const { t } = useBundleTranslation('components/common/navbar/page_title_and_breadcrumbs');
    const dispatch = useDispatch();
    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const breadcrumbsData = useSelector((state: any) => state.breadcrumbs);
    const needSetRecentItem = useRef<boolean>(false);

    const [initFrom, setInitFrom] = useState<string>('');
    const [needToCheckLicense, setNeedToCheckLicense] = useState<boolean>(true);
    const [warnMismatchLoginPopupShow, setWarnMismatchLoginPopupShow] = useState<boolean>(
        !!userAuth?.userInfo?.usernameLoggedInSamlAsMi && userAuth.userInfo.usernameLoggedInSamlAsMi !== '',
    );
    const [licensePopupShow, setLicensePopupShow] = useState<boolean>(false);
    const [licensePopupResponse, setLicensePopupResponse] = useState<any>({});

    const matches = useMatches();
    const routeDefinition: any = Array.isArray(matches) && matches.length ? matches[matches.length - 1] : undefined;
    const routeData: any = routeDefinition?.handle;
    const location = useLocation();

    const pageTitle = useAppSelector((state) => state.pageTitle.title);
    const portalName = useSelector((state: any) => state.auth?.userInfo?.portalName);

    const isEnableAppleAppBanner = useIsConfigConstantEqual('ENABLE_APPLE_APP_BANNER', true);
    const mobileAppAppleId = useConfigConstantValue('MOBILE_APP_APPLE_ID');

    const isPP = useRef<boolean>(checkIsAppPage());

    const isAdmin = useIsAdmin();
    const isPowerUser = useIsPowerUser();

    //Set up Apple banner
    useEffect(() => {
        if (isEnableAppleAppBanner && mobileAppAppleId > '') {
            let meta = document.createElement('meta');
            meta.name = 'apple-itunes-app';
            meta.content = `app-id=${mobileAppAppleId}`;
            document.getElementsByTagName('head')[0].appendChild(meta);
        }

        //Don't set favicons on PP (it's set in PP layout)
        if (isPP.current) return;

        const favicons: any = userAuth?.userInfo?.faviconsList ?? [];
        const defaultIcons = ['ico', '16x16', '32x32', '48x48'];
        defaultIcons.forEach((type: string) => {
            if (favicons.hasOwnProperty(type)) {
                const icon = favicons[type];
                let link = document.createElement('link');
                link.rel = type == 'ico' ? 'shortcut icon' : 'icon';
                link.setAttribute('sizes', icon.sizes);
                link.type = icon.type;
                link.href = icon.src;
                document.getElementsByTagName('head')[0].appendChild(link);
            }
        });
    }, []);

    //Set up page menu
    useEffect(() => {
        dispatch(setPageMenu(routeData?.pageMenu));
    }, [routeData?.pageMenu]);

    //Set up help link for User Menu
    useEffect(() => {
        dispatch(setHelpLink(routeData?.helpLink));
    }, [routeData?.helpLink]);

    //Set up common breadcrumbs
    useEffect(() => {
        if (routeData?.breadcrumbs !== false) {
            const breadcrumbs = routeData?.breadcrumbs?.list ?? [{ label: routeData?.name }];

            dispatch(setBreadcrumbs(breadcrumbs));
            dispatch(setBreadcrumbsInstanceKey(routeData?.breadcrumbs?.instanceDataKey));
        } else dispatch(removeBreadcrumbs());
    }, [routeData?.name]);

    //set common page title
    useEffect(() => {
        //Don't set page title on PP (it's set in PP layout)
        if (isPP.current) return;

        if (routeData?.pageTitle !== false) {
            let pageTitle = routeData?.pageTitle?.value ?? routeData?.name;

            //false value will use portalName if exist else empty ''
            if (routeData?.pageTitle?.value === false) {
                pageTitle = '';
            }
            dispatch(setPageTitle(t(pageTitle)));
            dispatch(setPageTitleInstanceKey(routeData?.pageTitle?.instanceDataKey));
        } else dispatch(removePageTitle());
    }, [routeData?.name]);

    //change pageTitle in store
    useEffect(() => {
        //Don't set page title on PP (it's set in PP layout)
        if (isPP.current) return;

        let pageTitleValue = '';
        if (pageTitle !== false) {
            const portalNameBrand = portalName > '' ? portalName : '';
            pageTitleValue = pageTitle > '' ? pageTitle : portalNameBrand;
        }
        document.title = pageTitleValue;
    }, [pageTitle]);

    useEffect(() => {
        isPP.current = checkIsAppPage();

        //manage movement between main app and pp app
        if (
            //Click to PP from main app by route
            isPP.current &&
            initFrom === 'mainApp'
        ) {
            window.location.reload();
        }

        if (!initFrom) {
            //setup on load init environment app
            setInitFrom(isPP.current ? 'portalPage' : 'mainApp');
        }
    }, [location]);

    const trackRecentItems = () => {
        const trackRouteUidEntities = [
            'app-editor',
            'announcement-editor',
            'document-editor',
            'kpi-alert-rule-editor',
            'tag-editor',
            'glossary-term-editor',
            'custom-field-editor',
            'category-editor',
            'folder-editor',
            'burst-editor',
            'dataset-editor',
            'metric-editor',
            'report-editor',
            'external-report-editor',
            'external-content-editor',
            'dataset-viewer',
            'metric-viewer',
            'report-viewer',
            'external-report-viewer',
            'external-content-viewer',
        ];
        if (routeData?.routeUid && trackRouteUidEntities.includes(routeData?.routeUid)) {
            needSetRecentItem.current = true;
        }
    };

    useEffect(() => {
        if (breadcrumbsData.currentElement && needSetRecentItem.current) {
            const routeUid = routeData?.routeUid ? routeData.routeUid : '';
            const isViewer = [
                'dataset-viewer',
                'metric-viewer',
                'report-viewer',
                'external-report-viewer',
                'external-content-viewer',
            ].includes(routeUid);
            const replaceRoute = isViewer ? '-viewer' : '-editor';

            const type = routeUid.replace(replaceRoute, '');
            let name = breadcrumbsData.currentElement.label ?? '';
            if (type == 'kpi-alert-rule') {
                name = breadcrumbsData.items[breadcrumbsData.items.length - 1].label + '\: ' + name;
            }

            const viewerIdProp = routeUid == 'dataset-viewer' ? 'datasetId' : 'elementId';
            const idVal = isViewer
                ? (routeDefinition?.params ?? {})[viewerIdProp]
                : Object.values(routeDefinition?.params ?? {})?.[0];
            const id = isNaN(Number(idVal)) ? 0 : Number(idVal);

            const userId =
                userAuth?.userInfo?.hijack_user_id && userAuth?.userInfo?.hijack_user_id > 0
                    ? userAuth.userInfo.hijack_user_id
                    : (userAuth?.userInfo?.user_id ?? 0);
            if (type && name && id != 0 && userId > 0) {
                addCommandMenuNewRecentItem(userId, {
                    object_type: type,
                    item_id: id,
                    name: name,
                });
            }

            needSetRecentItem.current = false;
        }
    }, [breadcrumbsData.currentElement]);

    useEffect(() => {
        trackRecentItems();
        if (
            (process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT === 'development') ||
            !userAuth.isLoggedIn
        )
            return;
        if (needToCheckLicense) {
            setLicensePopupShow(false);
            setLicensePopupResponse({});
            licenseKeyAPI
                .checkLicense()
                .then((response) => {
                    if (response?.status === 200) {
                        setLicensePopupResponse(response.data);
                        const isLicenseBad =
                            !response.data?.success || response.data?.expired == 1 || response.data?.trial;
                        setLicensePopupShow(isLicenseBad);
                        if (!isLicenseBad) {
                            setNeedToCheckLicense(false);
                            setTimeout(function () {
                                setNeedToCheckLicense(true);
                            }, 10800000); //3h
                        }
                    }
                })
                .catch(() => {});
        }
    }, [routeData?.name]);

    if (!userAuth.isLoggedIn && !routeData?.isPublic) {
        try {
            if (
                userAuth?.configuration?.constants?.IFRAME_AUTH_PARENT_REDIRECT &&
                undefined !== window.top &&
                undefined !== window.self &&
                window.self !== window.top
            ) {
                window?.top?.document?.location.reload();
                return null;
            }
        } catch (e) {}

        return <LoginRedirect />;
    }

    if (
        !routeData?.isPublic &&
        userAuth?.userInfo?.isChangePasswordRequired &&
        routeDefinition?.pathname != '/auth/change'
    ) {
        return <Navigate to="/auth/change" />;
    }

    return (
        <>
            <Outlet />
            <MobilePagePopup />
            {warnMismatchLoginPopupShow && (!routeData?.isPublic || isPP.current) && (
                <WarnMismatchLoginPopup
                    onHide={() => {
                        setWarnMismatchLoginPopupShow(false);
                        dispatch(setUserInfo({ usernameLoggedInSamlAsMi: '' }));
                    }}
                />
            )}
            {(isAdmin || isPowerUser) && (!routeData?.isPublic || isPP.current) && <CommandMenu />}
            {licensePopupShow && (!routeData?.isPublic || isPP.current) ? (
                <LicensePopup
                    response={licensePopupResponse}
                    onHide={() => {
                        setLicensePopupShow(false);
                    }}
                />
            ) : (
                <TourManager />
            )}
        </>
    );
}
